import React, { useState, useEffect } from "react";
import { Input, Tag, Button, Space } from "antd";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";

interface TextInputProps {
  id?: string;
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  placeholder?: string;
  multiple?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  className?: string;
  onBlur?: () => void;
  onPressEnter?: () => void;
  allowClear?: boolean;
  addonAfter?: React.ReactNode;
  addonBefore?: React.ReactNode;
  prefix?: React.ReactNode;
  size?: "large" | "middle" | "small";
  suffix?: React.ReactNode;
  type?: string;
  status?: "error" | "warning";
  bordered?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;
  required?: boolean;
}

const TextInput: React.FC<TextInputProps> = ({
  value,
  onChange,
  placeholder = "",
  multiple = false,
  disabled = false,
  style = {},
  className = "",
  onBlur,
  onPressEnter,
  allowClear = true,
  addonAfter,
  addonBefore,
  prefix,
  size = "middle",
  suffix,
  type = "text",
  status,
  bordered = true,
  readOnly = false,
  ...rest
}) => {
  const [inputValue, setInputValue] = useState("");
  const [values, setValues] = useState<string[]>([]);
  const [isFocused, setIsFocused] = useState(false);
  const inputId = React.useMemo(
    () => `text-input-${Math.random().toString(36).substr(2, 9)}`,
    [],
  );

  // Initialize values from props
  useEffect(() => {
    if (multiple) {
      if (Array.isArray(value)) {
        setValues([...value]);
      } else if (value) {
        setValues([String(value)]);
      } else {
        setValues([]);
      }
    }
  }, [value, multiple]);

  const handleAddValue = () => {
    if (!inputValue.trim()) return;

    const newValues = [...values, inputValue.trim()];
    setValues(newValues);
    setInputValue("");

    if (onChange) {
      onChange(newValues);
    }
  };

  const handleRemoveValue = (index: number) => {
    const newValues = values.filter((_, i) => i !== index);
    setValues(newValues);

    if (onChange) {
      onChange(newValues);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      handleAddValue();
    } else if (e.key === "Backspace" && !inputValue && values.length > 0) {
      // Remove last value on backspace when input is empty
      handleRemoveValue(values.length - 1);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (onBlur) onBlur();

    // Add value on blur if there's text in the input
    if (multiple && inputValue.trim()) {
      handleAddValue();
    }
  };

  const handlePressEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (onPressEnter) {
      onPressEnter();
    }

    if (multiple && inputValue.trim()) {
      e.preventDefault();
      handleAddValue();
    }
  };

  if (multiple) {
    return (
      <div
        className={`ant-input ${className} ${isFocused ? "ant-input-focused" : ""} ${status ? `ant-input-status-${status}` : ""}`}
        style={{
          minHeight:
            size === "large" ? "40px" : size === "small" ? "24px" : "32px",
          padding: "4px 11px",
          border: bordered ? "1px solid #d9d9d9" : "none",
          borderRadius: "6px",
          backgroundColor: disabled ? "#f5f5f5" : "#fff",
          cursor: disabled ? "not-allowed" : "text",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "4px",
          ...style,
        }}
        onClick={() => !disabled && document.getElementById(inputId)?.focus()}
      >
        {addonBefore && (
          <span className="ant-input-group-addon">{addonBefore}</span>
        )}
        {prefix && <span className="ant-input-prefix">{prefix}</span>}

        <Space
          size={[4, 4]}
          wrap
          style={{
            flex: 1,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {values.map((val, index) => (
            <Tag
              key={`${val}-${index}`}
              closable={!disabled}
              onClose={(e) => {
                e.preventDefault();
                handleRemoveValue(index);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                margin: 0,
                ...(disabled ? { padding: "0 7px", border: "none" } : {}),
              }}
            >
              {val}
            </Tag>
          ))}
          <Space.Compact style={{ flex: 1, minWidth: "100px" }}>
            <Input
              id={inputId}
              type={type}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsFocused(true)}
              onBlur={handleBlur}
              onPressEnter={handlePressEnter}
              disabled={disabled}
              placeholder={values.length === 0 ? placeholder : ""}
              style={{
                flex: 1,
                minWidth: "60px",
                border: "none",
                boxShadow: "none",
                padding: 0,
                height: "auto",
                background: "transparent",
              }}
              readOnly={readOnly}
              {...rest}
            />
            {inputValue && (
              <Button
                type="text"
                size="small"
                icon={<PlusOutlined />}
                onClick={handleAddValue}
                style={{ display: "flex", alignItems: "center" }}
              />
            )}
          </Space.Compact>
        </Space>

        <Space size="small" style={{ marginLeft: "auto" }}>
          {suffix && <span className="ant-input-suffix">{suffix}</span>}
          {allowClear && !disabled && (values.length > 0 || inputValue) && (
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                setValues([]);
                setInputValue("");
                if (onChange) onChange([]);
              }}
              style={{ opacity: 0.6 }}
            />
          )}
        </Space>

        {addonAfter && (
          <span className="ant-input-group-addon">{addonAfter}</span>
        )}
      </div>
    );
  }

  // Single value mode
  return (
    <Input
      value={value as string}
      onChange={(e) => onChange && onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      style={style}
      className={className}
      onBlur={onBlur}
      onPressEnter={onPressEnter}
      allowClear={allowClear}
      addonAfter={addonAfter}
      addonBefore={addonBefore}
      prefix={prefix}
      size={size}
      suffix={suffix}
      type={type}
      status={status}
      bordered={bordered}
      readOnly={readOnly}
      {...rest}
    />
  );
};

export default TextInput;
